import { EmbedAppContentModel, getContent } from "client/Api/Apps/Embed/embedApp";
import { HttpRequestError } from "client/Api/httpClient";
import { useSynchronizedRef } from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	RefObject,
	useEffect,
	useMemo,
	useState
} from "react";

export type ContentLoadingState = { appId: number; }
	& (
		| { kind: "not-started"; }
		| {
			kind: "pending";
			cancellation: AbortController;
		}
		| { kind: "forbidden"; }
		| { kind: "error"; }
		| {
			kind: "loaded";
			model: EmbedAppContentModel;
		}
	);

export function useEmbedContentLoading(
	environmentAppId: number,
	isAvailable: boolean
): ContentLoadingState {
	const [contentLoadingState, setContentLoadingState] = useState<ContentLoadingState>({
		appId: environmentAppId,
		kind: "not-started"
	});

	const contentLoadingStateRef: RefObject<ContentLoadingState> = useSynchronizedRef(contentLoadingState);

	useEffect(
		(): (() => void) => {
			return (): void => {
				const { current: contentLoadingState } = contentLoadingStateRef;

				if (contentLoadingState.kind === "pending") {
					contentLoadingState.cancellation
						.abort();
				}
			};
		},
		[contentLoadingStateRef]
	);

	useEffect(
		(): void => {
			const { current: contentLoadingState } = contentLoadingStateRef;

			const wasAppIdChanged: boolean = contentLoadingState.appId !== environmentAppId;

			const shouldLoad: boolean = isAvailable
				&& (
					wasAppIdChanged || contentLoadingState.kind === "not-started"
				);

			if (!shouldLoad) {
				return;
			}

			if (contentLoadingState.kind === "pending") {
				contentLoadingState.cancellation
					.abort();
			}

			const cancellation = new AbortController();

			setContentLoadingState({
				appId: environmentAppId,
				kind: "pending",
				cancellation
			});

			const load = async (appId: number, signal: AbortSignal): Promise<void> => {
				try {
					const model: EmbedAppContentModel = await getContent(appId, signal);

					setContentLoadingState({
						appId,
						kind: "loaded",
						model
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					if (e instanceof HttpRequestError && e.statusCode === 403) {
						setContentLoadingState({
							appId,
							kind: "forbidden"
						});
					} else {
						setContentLoadingState({
							appId,
							kind: "error"
						});
					}
				}
			};

			load(environmentAppId, cancellation.signal);
		},
		[
			contentLoadingStateRef,
			environmentAppId,
			isAvailable
		]
	);

	return useMemo(
		(): ContentLoadingState => {
			if (!isAvailable
				// Since we use an effect to update the state, it will be stale during the render
				// where the appId changes, so we normalize that slight inconsistency away
				|| contentLoadingState.appId !== environmentAppId
			) {
				return { kind: "not-started", appId: environmentAppId };
			}

			return contentLoadingState;
		},
		[
			contentLoadingState,
			environmentAppId,
			isAvailable
		]
	);
}